/**
 * Facebook Effective Status values and their human-readable explanations.
 *
 * effective_status represents the actual delivery state of an entity,
 * taking into account parent entity statuses, review status, etc.
 *
 * status = user intent (what you set)
 * effective_status = reality (what's actually happening)
 */

export type EffectiveStatus =
  // Active states
  | 'ACTIVE'
  | 'IN_PROCESS'
  // Paused states (by user)
  | 'PAUSED'
  // Paused states (inherited from parent)
  | 'CAMPAIGN_PAUSED'
  | 'ADSET_PAUSED'
  // Review states
  | 'PENDING_REVIEW'
  | 'PENDING_BILLING_INFO'
  | 'PREAPPROVED'
  // Disapproved/Error states
  | 'DISAPPROVED'
  | 'WITH_ISSUES'
  // Archived/Deleted states
  | 'ARCHIVED'
  | 'DELETED';

export interface StatusInfo {
  label: string;
  description: string;
  color: 'success' | 'warning' | 'error' | 'default' | 'info';
  icon?: string;
}

/**
 * Maps effective_status values to human-readable information.
 * Each status includes:
 * - label: Short display text
 * - description: Full explanation of why this status is set
 * - color: MUI color for visual indication
 */
export const STATUS_INFO: Record<EffectiveStatus | string, StatusInfo> = {
  // Active states
  ACTIVE: {
    label: 'Active',
    description: 'Running normally and eligible to deliver',
    color: 'success',
  },
  IN_PROCESS: {
    label: 'Processing',
    description: 'Being processed by Facebook, will be active soon',
    color: 'info',
  },

  // Paused states (user action)
  PAUSED: {
    label: 'Paused',
    description: 'You paused this entity',
    color: 'warning',
  },

  // Paused states (inherited from parent)
  CAMPAIGN_PAUSED: {
    label: 'Campaign Paused',
    description: 'The parent campaign is paused. Activate the campaign to resume delivery.',
    color: 'warning',
  },
  ADSET_PAUSED: {
    label: 'Ad Set Paused',
    description: 'The parent ad set is paused. Activate the ad set to resume delivery.',
    color: 'warning',
  },

  // Review states
  PENDING_REVIEW: {
    label: 'Pending Review',
    description: 'Awaiting review by Facebook. This usually takes less than 24 hours.',
    color: 'info',
  },
  PENDING_BILLING_INFO: {
    label: 'Billing Issue',
    description: 'Facebook needs billing information to run this ad. Update your payment method.',
    color: 'error',
  },
  PREAPPROVED: {
    label: 'Pre-approved',
    description: 'Approved but not yet active. Will start when conditions are met.',
    color: 'info',
  },

  // Disapproved/Error states
  DISAPPROVED: {
    label: 'Disapproved',
    description: 'Rejected by Facebook for policy violations. Edit the ad to comply with policies.',
    color: 'error',
  },
  WITH_ISSUES: {
    label: 'Issues',
    description: 'Has issues that may affect delivery. Check the Facebook Ads Manager for details.',
    color: 'error',
  },

  // Archived/Deleted states
  ARCHIVED: {
    label: 'Archived',
    description: 'Archived and no longer active. Can be unarchived if needed.',
    color: 'default',
  },
  DELETED: {
    label: 'Deleted',
    description: 'Deleted and cannot be restored',
    color: 'default',
  },
};

/**
 * Get status information with fallback for unknown statuses.
 */
export function getStatusInfo(status: string | null | undefined): StatusInfo {
  if (!status) {
    return {
      label: 'Confirming',
      description: 'Created on Facebook; the next import will say what it is doing',
      color: 'default',
    };
  }

  return STATUS_INFO[status] || {
    label: status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()),
    description: `Status: ${status}`,
    color: 'default',
  };
}

/**
 * Simple status colors for the local status (user intent).
 * Used when showing just the user's intended status without effective_status context.
 */
export const LOCAL_STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  ACTIVE: 'success',
  PAUSED: 'warning',
  DELETED: 'error',
  ARCHIVED: 'default',
};

/**
 * Check if an entity is effectively running (can deliver ads).
 */
export function isEffectivelyActive(effectiveStatus: string | null | undefined): boolean {
  return effectiveStatus === 'ACTIVE' || effectiveStatus === 'IN_PROCESS';
}

/**
 * Check if an entity has issues that need attention.
 */
export function hasIssues(effectiveStatus: string | null | undefined): boolean {
  return effectiveStatus === 'DISAPPROVED' ||
         effectiveStatus === 'WITH_ISSUES' ||
         effectiveStatus === 'PENDING_BILLING_INFO';
}

/**
 * Check if an entity is paused due to a parent entity.
 */
export function isPausedByParent(effectiveStatus: string | null | undefined): boolean {
  return effectiveStatus === 'CAMPAIGN_PAUSED' || effectiveStatus === 'ADSET_PAUSED';
}
